import { useRef, useEffect, memo, useCallback, useReducer } from "react";
import ME3DScene from "./ME3DScene";
import { ARCH_HEIGHT_DATA_ORDERED, FOAM_PACKAGE_SHOPIFY_OPTIONS, FOAM_PACKAGE_TYPES, FoamPackageType } from "../../../constants/foot";
import { useApp } from "../../../context/AppContext";
import { PROGRESS_STAGES_DATA } from "./constants";
import ProgressControl, { ProgressControlHandle } from "./ProgressControl";
import StudioSlot from "../../insole-studio/StudioSlot";
import ArchHeightTestControls, { FootSide } from "./ArchHeightTestControls";

const archHeightOptionsLabels = ARCH_HEIGHT_DATA_ORDERED.map(({ type }) => type);

const NOT_SCANNED_ARCH_INDEX = 4;

const PreviewInsole3DModelMain = memo(({
  leftArchIndex,
  rightArchIndex,
  selectedFoamPackageOption = FOAM_PACKAGE_SHOPIFY_OPTIONS['superrev'],
}: {
  leftArchIndex?: number;
  rightArchIndex?: number;
  selectedFoamPackageOption?: string;
}) => {
  // Using refs to avoid re-rendering the heavy 3D subtree on every frame.
  // triggerRender is called only on discrete user interactions (swipe/click)
  // so that React's reconciler commits into the Canvas and R3F's demand
  // scheduler picks up the change without needing an external invalidateRef.
  const { foamPackageShopifyOptions } = useApp();
  const [, triggerRender] = useReducer(x => x + 1, 0);
  const stageRef = useRef(PROGRESS_STAGES_DATA[0].stage);
  const leftArchIndexRef = useRef<number | undefined>(leftArchIndex || NOT_SCANNED_ARCH_INDEX);
  const rightArchIndexRef = useRef<number | undefined>(rightArchIndex || NOT_SCANNED_ARCH_INDEX);
  const isLeftArchScannedRef = useRef<boolean>(Boolean(leftArchIndex));
  const isRightArchScannedRef = useRef<boolean>(Boolean(rightArchIndex));

  // Updating the ref AND triggering a React re-render so R3F's reconciler
  // commits and internally calls invalidate() in frameloop="demand" mode.
  const setStage = useCallback((newStage: number) => {
    stageRef.current = newStage;
    triggerRender();
  }, []);

  // Only ever set by the dev dropdown below, so production behaviour is
  // unchanged. Stops the prop-sync effect from wiping a manual override when
  // an unrelated dep changes (e.g. switching foam package).
  const archOverrideRef = useRef(false);

  // Same ref-then-triggerRender pattern as setStage: the value lives in a ref
  // so the 3D subtree isn't re-rendered per frame, and the render commit is
  // what wakes R3F's demand frameloop so the bulge actually animates.
  const setArchIndex = useCallback((side: FootSide, archIndex: number) => {
    archOverrideRef.current = true;
    if (side === 'left') {
      leftArchIndexRef.current = archIndex;
      isLeftArchScannedRef.current = true;
    } else {
      rightArchIndexRef.current = archIndex;
      isRightArchScannedRef.current = true;
    }
    triggerRender();
  }, []);

  const progressControlRef = useRef<ProgressControlHandle>(null);

  useEffect(() => {
    if (archOverrideRef.current) return;
    leftArchIndexRef.current = leftArchIndex || NOT_SCANNED_ARCH_INDEX;
    rightArchIndexRef.current = rightArchIndex || NOT_SCANNED_ARCH_INDEX;
    isLeftArchScannedRef.current = Boolean(leftArchIndex);
    isRightArchScannedRef.current = Boolean(rightArchIndex);
  }, [leftArchIndex, rightArchIndex, selectedFoamPackageOption]);

  const foamPackageType: FoamPackageType = selectedFoamPackageOption.toLowerCase() === foamPackageShopifyOptions[FOAM_PACKAGE_TYPES.SUPERREV].toLowerCase() ? FOAM_PACKAGE_TYPES.SUPERREV : FOAM_PACKAGE_TYPES.SUPERREV_ELITE;

  return (
    <>
      <ME3DScene
        foamPackageType={foamPackageType}
        stageRef={stageRef}
        controls={{
          leftArchIndex: leftArchIndexRef,
          rightArchIndex: rightArchIndexRef,
          isLeftArchScanned: isLeftArchScannedRef,
          isRightArchScanned: isRightArchScannedRef,
          archNamePlaceholder: 'Select Arch Height',
          archNames: archHeightOptionsLabels,
          leftStiffnessDir: { current: 0 },
          rightStiffnessDir: { current: 0 },
          stiffnessDirPlaceholder: '',
          leftZones: { current: undefined },
          rightZones: { current: undefined },
        }}
      />
      <StudioSlot name="canvasOverlay">
        <ProgressControl ref={progressControlRef} stageRef={stageRef} setStage={setStage} />
      </StudioSlot>
      <ArchHeightTestControls
        defaultArchIndex={NOT_SCANNED_ARCH_INDEX}
        onChange={setArchIndex}
      />
    </>
  );
});

PreviewInsole3DModelMain.displayName = 'PreviewInsole3DModelMain';

export default PreviewInsole3DModelMain;
